import React, { useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth, authenticatedFetch } from '../hooks/useAuth';
import { useI18n } from '../i18n/index.jsx';

// Simple ID generator
const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const frDate = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const opts = sameYear
    ? { day: 'numeric', month: 'long' }
    : { day: 'numeric', month: 'long', year: 'numeric' };
  return new Intl.DateTimeFormat('fr-FR', opts).format(d);
};

export default function Souvenirs() {
  const { user } = useAuth();
  const { t } = useI18n();

  const [items, setItems] = useState([]); // {id, content, createdAt}
  const [quickText, setQuickText] = useState('');
  // Import modal state
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const [showConfirmAll, setShowConfirmAll] = useState(false);
  const [confirmAllText, setConfirmAllText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const filtered = items;

  // Fetch depuis l'API
  async function fetchMemories() {
    try {
      setLoading(true);
      setError('');
  const res = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/memory/all', { method: 'GET' });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      const data = await res.json();
      const raw = Array.isArray(data) ? data : (Array.isArray(data?.memories) ? data.memories : (Array.isArray(data?.data) ? data.data : []));
      const mapped = raw.map((m) => ({
        id: m.id_memory ?? m.id ?? makeId(),
        content: m.phrase || m.content || m.texte || '',
        createdAt: m.created_at || m.createdAt || new Date().toISOString(),
        source: m.source || 'api'
      }));
      setItems(mapped);
    } catch (e) {
      console.error('Souvenirs fetch error:', e);
      setError(e.message || 'Erreur lors du chargement des souvenirs');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user) fetchMemories();
  }, [user]);
  

  // No search bar: filtered is identical to items

  const grouped = useMemo(() => {
    const byDay = new Map();
    for (const it of filtered) {
      const label = frDate(it.createdAt || it.date || new Date().toISOString());
      if (!byDay.has(label)) byDay.set(label, []);
      byDay.get(label).push(it);
    }
    return Array.from(byDay.entries());
  }, [filtered]);

  async function addMemoryLine(text) {
    const body = JSON.stringify({ texte: text, source: 'manual' });
  const res = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/memory/add_long', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
    const data = await res.json();
    // Optionnel: on pourrait extraire l'élément créé si renvoyé.
    return data;
  }

  async function addQuick() {
    const raw = quickText;
    if (!raw || !raw.trim()) return;
    const lines = raw.split(/\r?\n/)
      .map(l => l.replace(/^\s*[•*\-]\s+/, '').trim())
      .filter(Boolean);
    if (lines.length === 0) return;
    try {
      setLoading(true);
      setError('');
      // Envoie chaque ligne à l'API
      for (const line of lines) {
        await addMemoryLine(line);
      }
      setQuickText('');
      await fetchMemories();
    } catch (e) {
      console.error('Souvenir add error:', e);
      setError(e.message || 'Erreur lors de l\'ajout');
    } finally {
      setLoading(false);
    }
  }

  async function removeItem(id) {
    try {
      setLoading(true);
      setError('');
      const deleted_id = Number.isFinite(Number(id)) ? Number(id) : id;
  const res = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/memory/remove', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_memory: deleted_id })
      });
      if (!res.ok) {
        const bodyText = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${res.statusText} ${bodyText}`);
      }
      const data = await res.json().catch(() => ({}));
      await fetchMemories();
    } catch (e) {
      console.error('Souvenir delete error:', e);
      setError(e.message || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  }

  async function clearAll() {
    if (!confirm('Supprimer tous les souvenirs via l\'API ?')) return;
    try {
      setLoading(true);
      setError('');
      for (const it of items) {
        try { await removeItem(it.id); } catch {}
      }
      await fetchMemories();
    } catch (e) {
      setError('Erreur lors de la suppression de masse');
    } finally {
      setLoading(false);
    }
  }

  function onImportClick() {
    setShowImport(true);
    setImportError('');
    setImportText('');
  }

  // Removed JSON file import handler

  async function handleImportText() {
    setImportError('');
    const raw = importText.trim();
    if (!raw) {
      setImportError('Veuillez coller du texte ou du JSON.');
      return;
    }
    // On supporte soit un JSON (memories/items/data) soit 1 ligne = 1 souvenir
    let lines = [];
    try {
      const parsed = JSON.parse(raw);
      const arr = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.memories)
        ? parsed.memories
        : Array.isArray(parsed?.items)
        ? parsed.items
        : Array.isArray(parsed?.data)
        ? parsed.data
        : [parsed];
      lines = normalizeImportedArray(arr).map(x => x.content);
    } catch {
      lines = raw
        .split(/\r?\n/)
        .map(line => line.replace(/^\s*[•*\-]\s+/, '').trim())
        .filter(Boolean);
    }

    if (lines.length === 0) {
      setImportError('Impossible de parser le contenu collé.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      for (const line of lines) {
        await addMemoryLine(line);
      }
      setShowImport(false);
      setImportText('');
      await fetchMemories();
    } catch (e) {
      console.error('Souvenirs import error:', e);
      setImportError(e.message || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  }

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto">
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
        <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

        <div className="relative z-10 py-16 px-5 sm:px-6">
          <div className="mx-auto max-w-4xl">
            {/* Top bar */}
            <div className="mb-6 flex items-center gap-3">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold">{t('souvenirs.title')}</h1>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={onImportClick} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200 hover:bg-white/10">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="1.5" d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16"/></svg>
                  Importer
                </button>
              </div>
            </div>

            {/* Quick input */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-2 backdrop-blur-2xl mb-6">
              <div className="flex items-center gap-2">
                <input
                  value={quickText}
                  onChange={(e)=>setQuickText(e.target.value)}
                  placeholder="Écrivez pour ajouter un nouveau souvenir"
                  className="flex-1 rounded-xl border border-transparent bg-slate-900/40 px-4 py-3 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
                />
                <button onClick={addQuick} className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-medium text-slate-950 hover:bg-emerald-400">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="2" d="M12 5v14M5 12h14"/></svg>
                  Ajouter
                </button>
              </div>
            </div>

            {/* List */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-2xl">
        <div className="flex items-center justify-between mb-4">
                <div className="text-slate-300 text-sm">{filtered.length} souvenirs</div>
                {filtered.length > 0 && (
          <button onClick={()=>{ setShowConfirmAll(true); setConfirmAllText(''); }} className="text-sm text-red-300 hover:text-red-200">Supprimer tout</button>
                )}
              </div>

              {error && (
                <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300 flex items-center justify-between">
                  <span>{error}</span>
                  <button onClick={fetchMemories} className="underline">Réessayer</button>
                </div>
              )}

              {loading && (
                <div className="flex items-center justify-center py-6 text-slate-400 text-sm">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-400"></div>
                  <span className="ml-2">Chargement…</span>
                </div>
              )}

              {grouped.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/15 bg-white/5 p-8 text-center text-slate-400 text-sm">
                  Aucun souvenir pour le moment. Ajoutez votre premier souvenir ci-dessus.
                </div>
              ) : (
                <div className="space-y-8">
                  {grouped.map(([label, arr]) => (
                    <div key={label}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="h-2 w-2 rounded-full bg-emerald-400/80"></span>
                        <div className="text-sm text-slate-300">{label}</div>
                      </div>
                      <div className="space-y-3">
                        {arr.map(it => (
                          <div key={it.id} className="group relative rounded-xl border border-white/10 bg-white/5 p-4">
                            {/* Hover delete icon */}
                            <button
                              onClick={() => removeItem(it.id)}
                              title="Supprimer"
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity rounded-md p-1.5 text-red-300 hover:text-red-200 hover:bg-red-500/10 border border-transparent hover:border-red-500/20"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeWidth="1.8" d="M3 6h18"/>
                                <path strokeWidth="1.8" d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                <path strokeWidth="1.8" d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path strokeWidth="1.8" d="M10 11v6M14 11v6"/>
                              </svg>
                            </button>

                            <p className="whitespace-pre-wrap leading-relaxed text-slate-200 text-sm pr-8">{it.content}</p>
                            <div className="mt-3 flex items-center justify-end text-xs text-slate-500">
                              <span>{new Date(it.createdAt || Date.now()).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Import modal */}
            {showImport && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={()=>setShowImport(false)} />
                <div className="relative z-10 w-[92vw] max-w-xl rounded-2xl border border-white/10 bg-slate-900/80 p-5 text-slate-200 shadow-2xl">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="h-4 w-4 rounded-sm bg-gradient-to-br from-amber-400 to-pink-500" />
                    <h2 className="text-lg font-semibold">Importer depuis ChatGPT</h2>
                  </div>

                  <ol className="space-y-4 text-sm">
                    <li>
                      <div className="font-medium">
                        1
                        <span className="ml-2 underline decoration-emerald-400/60 decoration-2 underline-offset-2">
                          <a href="https://chat.openai.com/" target="_blank" rel="noreferrer" className="hover:text-white">Voir les souvenirs dans ChatGPT</a>
                        </span>
                      </div>
                      <div className="mt-1 text-slate-400">Cliquez sur profil → Paramètres → Personnalisation → Gérer les éléments mémorisés</div>
                    </li>
                    
                    <li>
                      <div className="font-medium">2 <span className="ml-2">Copiez-les et collez ci-dessous</span></div>
                      <div className="mt-1 text-slate-400">Une ligne = un souvenir (importation instantanée)</div>
                      <textarea
                        value={importText}
                        onChange={(e)=>{ setImportText(e.target.value); setImportError(''); }}
                        placeholder="Collez vos souvenirs ici (une ligne = un souvenir)..."
                        className="mt-3 h-40 w-full resize-y rounded-lg border border-white/10 bg-slate-950/60 px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      {importError && (
                        <div className="mt-2 text-xs text-red-300">{importError}</div>
                      )}
                    </li>
                  </ol>

                  <div className="mt-4 flex items-center justify-between">
                    <button onClick={()=>setShowImport(false)} className="rounded-lg bg-white/5 px-4 py-2 text-sm text-slate-200 hover:bg-white/10">Fermer</button>
                    <div className="flex items-center gap-2">
                      <button onClick={handleImportText} className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-slate-950 hover:bg-emerald-400">Importer</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showConfirmAll && (
              <div className="fixed inset-0 z-[110] flex items-center justify-center">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={()=>setShowConfirmAll(false)} />
                <div className="relative z-10 w-[92vw] max-w-md rounded-2xl border border-white/10 bg-slate-900/80 p-5 text-slate-200 shadow-2xl">
                  <h3 className="text-lg font-semibold">Confirmer la suppression</h3>
                  <p className="mt-2 text-sm text-slate-400">Pour supprimer <span className="font-semibold text-slate-200">tous</span> les souvenirs, tapez <span className="font-mono text-slate-100">SUPRIMER</span> ci-dessous.</p>
                  <input
                    autoFocus
                    value={confirmAllText}
                    onChange={e=>setConfirmAllText(e.target.value)}
                    placeholder="SUPRIMER"
                    className="mt-4 w-full rounded-lg border border-white/10 bg-slate-950/60 px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <div className="mt-5 flex items-center justify-between">
                    <button onClick={()=>setShowConfirmAll(false)} className="rounded-lg bg-white/5 px-4 py-2 text-sm text-slate-200 hover:bg-white/10">Annuler</button>
                    <button
                      disabled={confirmAllText.trim() !== 'SUPRIMER'}
                      onClick={async ()=>{ setShowConfirmAll(false); setConfirmAllText(''); await clearAll(); }}
                      className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Supprimer tout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// Helpers for import text parsing
function normalizeImportedArray(arr) {
  return arr.map(x => ({
    id: x.id || makeId(),
    title: x.title || (x.content ? String(x.content).slice(0, 77) + (String(x.content).length > 77 ? '…' : '') : ''),
    content: String(x.content || x.title || x.text || ''),
    createdAt: x.createdAt || new Date().toISOString(),
  })).filter(x => x.content.trim().length > 0);
}
